import type { TranslationBundle } from '@bms/common';

/** ReportsPage — hotel KPIs and alarm history for the front-office manager. */
export const reports: TranslationBundle = {
  en: {
    noSiteTitle: 'No site selected',
    noSiteDescription: 'Choose a site to see its reports.',
    notAvailableTitle: 'Reports is not available to this account',
    notAvailableDescription: 'This account cannot {{missing}} at this site. Ask a site administrator for access.',
    and: 'and',
    needRooms: 'see rooms and stays',
    needAlarms: 'see alarms',
    needReports: 'see hotel reports',

    windowSummary: 'Reporting on {{from}} to {{to}}',
    windowDefault: 'Hotel performance and alarm history for this site.',
    windowAria: 'Reporting window',
    window7: '7 days',
    window30: '30 days',
    window90: '90 days',
    windowShort7: '7 d',
    windowShort30: '30 d',
    windowShort90: '90 d',

    kpisTitle: 'Hotel KPIs',
    howComputed: 'How these are computed',
    medianDetail: '{{detail}} · median {{median}}',
    perDayTitle: 'Alarms per day',
    perDayCaption: '{{from}} – {{to}} · click a day to narrow the list',
    perDaySlot_one: '{{date}} · {{count}} alarm',
    perDaySlot_other: '{{date}} · {{count}} alarms',
    ofLoaded: 'of the {{loaded}} loaded',
    byTypeTitle: 'Alarms by type',
    byTypeCaption: 'the last {{days}} days',
    noAlarmsOfTypes: 'No alarms of type {{types}}.',
    severityCounts: '{{warning}} warning · {{critical}} critical · {{info}} info',
    kpisLoadFailed: 'The KPIs could not be loaded. {{message}}',
    retry: 'Retry',

    minutesShort: '{{value}} min',
    hoursShort: '{{hours}} hr',
    hoursMinutesShort: '{{hours}} hr {{minutes}} min',

    turnaroundTitle: 'Turnaround time',
    turnaroundHow: 'Average from the most recent checkout to a completed clean-room inspection.',
    turnaroundDetail_one: '{{count}} completed turnaround',
    turnaroundDetail_other: '{{count}} completed turnarounds',
    notEnoughData: 'Not enough data',
    notEnoughTurnarounds_one: 'not enough data ({{count}} turnaround)',
    notEnoughTurnarounds_other: 'not enough data ({{count}} turnarounds)',

    cleanThroughputTitle: 'Clean throughput',
    cleanThroughputValue: '{{value}} / day',
    cleanThroughputHow: 'Rooms moved from cleaning to clean, averaged across every day in the window.',
    cleanThroughputDetail_one: '{{count}} room cleaned',
    cleanThroughputDetail_other: '{{count}} rooms cleaned',

    dndTitle: 'DND rate',
    dndHow: 'Eligible check-ins that recorded Do Not Disturb before their next checkout.',
    dndDetail_one: '{{withDnd}} of {{count}} eligible stay',
    dndDetail_other: '{{withDnd}} of {{count}} eligible stays',

    alarmMttrTitle: 'Alarm MTTR',
    alarmMttrHow: 'Average from an alarm being raised to being resolved, for alarms resolved in the window.',
    alarmMttrDetail_one: '{{count}} resolved alarm',
    alarmMttrDetail_other: '{{count}} resolved alarms',
    notEnoughAlarms_one: 'not enough data ({{count}} resolved alarm)',
    notEnoughAlarms_other: 'not enough data ({{count}} resolved alarms)',
    otherAlarm: 'Other alarm',

    alarmHistoryTitle: 'Alarm history',
    alarmHistoryLoadFailed: 'The alarm history could not be loaded. {{message}}',
    alarmTypeFilterAria: 'Filter by alarm type',
    anyType: 'Any type',
    roomFilterPlaceholder: 'Room code',
    roomFilterAria: 'Filter by exact room code',
    alarmFromLabel: 'Alarm history from',
    alarmFromPlaceholder: 'From',
    alarmToLabel: 'Alarm history to',
    alarmToPlaceholder: 'To',
    rangeNarrowsList: 'This range narrows the alarm list only; the KPIs above are reported over the selected preset window.',
    rangeIncomplete: 'Pick both a start and an end date — one bound on its own is not a range, so the preset window is still in use.',

    noAlarmsTitle: 'No alarms in this range',
    noAlarmsDescription: 'Nothing was raised here over the window you chose.',
    loadMore: 'Load more',
    loadedOfTotal: '{{loaded}} of {{total}} shown',

    columnSeverity: 'Severity',
    raised: 'Raised',
    columnRoom: 'Room',
    columnAlarm: 'Alarm',
    acknowledgedByLabel: 'Acknowledged by',
    resolvedLabel: 'Resolved',
    durationLabel: 'Duration',
    // Beside the row's Open or Acknowledged tag: "Open … for 1 h 37 min".
    openFor: 'for {{duration}}',

    statusOpen: 'Open',
    statusAcknowledged: 'Acknowledged',

    // frontOfficeView.ts — alarmHistorySummary (a pure view model; takes
    // `tr: Translate = english`, mirroring Admin's model/translate.ts)
    frontOfficeSummaryCount_one: '{{count}} alarm in {{window}}',
    frontOfficeSummaryCount_other: '{{count}} alarms in {{window}}',
    frontOfficeWindowChosen: 'the chosen range',
    frontOfficeWindowDays: 'the last {{days}} days',
    frontOfficeStillOpen: '{{count}} still open',
    frontOfficeStillOpenOfLoaded: '{{count}} still open of the {{loaded}} loaded',
  },
  hr: {
    noSiteTitle: 'Nije odabrana lokacija',
    noSiteDescription: 'Odaberite lokaciju da biste vidjeli njezina izvješća.',
    notAvailableTitle: 'Izvješća nisu dostupna ovom računu',
    notAvailableDescription: 'Ovaj račun na ovoj lokaciji ne može {{missing}}. Zamolite administratora lokacije za pristup.',
    and: 'i',
    needRooms: 'vidjeti sobe i boravke',
    needAlarms: 'vidjeti alarme',
    needReports: 'vidjeti izvješća hotela',

    windowSummary: 'Izvještajno razdoblje: {{from}} – {{to}}',
    windowDefault: 'Uspješnost hotela i povijest alarma za ovu lokaciju.',
    windowAria: 'Razdoblje izvještaja',
    window7: '7 dana',
    window30: '30 dana',
    window90: '90 dana',
    windowShort7: '7 d',
    windowShort30: '30 d',
    windowShort90: '90 d',

    kpisTitle: 'KPI-jevi hotela',
    howComputed: 'Kako se ovo računa',
    medianDetail: '{{detail}} · medijan {{median}}',
    perDayTitle: 'Alarmi po danu',
    perDayCaption: '{{from}} – {{to}} · kliknite dan da suzite popis',
    perDaySlot_one: '{{date}} · {{count}} alarm',
    perDaySlot_few: '{{date}} · {{count}} alarma',
    perDaySlot_other: '{{date}} · {{count}} alarma',
    ofLoaded: 'od {{loaded}} učitanih',
    byTypeTitle: 'Alarmi po vrsti',
    byTypeCaption: 'posljednjih {{days}} dana',
    noAlarmsOfTypes: 'Nema alarma vrste {{types}}.',
    severityCounts: 'upozorenja: {{warning}} · kritičnih: {{critical}} · informativnih: {{info}}',
    kpisLoadFailed: 'KPI-jeve nije moguće učitati. {{message}}',
    retry: 'Pokušaj ponovno',

    minutesShort: '{{value}} min',
    hoursShort: '{{hours}} h',
    hoursMinutesShort: '{{hours}} h {{minutes}} min',

    turnaroundTitle: 'Vrijeme pripreme sobe',
    turnaroundHow: 'Prosjek od posljednjeg odjavljivanja do dovršenog pregleda očišćene sobe.',
    turnaroundDetail_one: '{{count}} dovršena priprema',
    turnaroundDetail_few: '{{count}} dovršene pripreme',
    turnaroundDetail_other: '{{count}} dovršenih priprema',
    notEnoughData: 'Nedovoljno podataka',
    notEnoughTurnarounds_one: 'nedovoljno podataka ({{count}} priprema)',
    notEnoughTurnarounds_few: 'nedovoljno podataka ({{count}} pripreme)',
    notEnoughTurnarounds_other: 'nedovoljno podataka ({{count}} priprema)',

    cleanThroughputTitle: 'Protok čišćenja',
    cleanThroughputValue: '{{value}} / dan',
    cleanThroughputHow: 'Sobe prebačene iz čišćenja u čisto, u prosjeku za svaki dan razdoblja.',
    cleanThroughputDetail_one: '{{count}} očišćena soba',
    cleanThroughputDetail_few: '{{count}} očišćene sobe',
    cleanThroughputDetail_other: '{{count}} očišćenih soba',

    dndTitle: 'Stopa „Ne ometaj”',
    dndHow: 'Prihvatljiva prijavljivanja koja su zabilježila „Ne ometaj” prije sljedeće odjave.',
    dndDetail_one: '{{withDnd}} od {{count}} prihvatljivog boravka',
    dndDetail_few: '{{withDnd}} od {{count}} prihvatljiva boravka',
    dndDetail_other: '{{withDnd}} od {{count}} prihvatljivih boravaka',

    alarmMttrTitle: 'Prosječno vrijeme rješavanja alarma',
    alarmMttrHow: 'Prosjek od podizanja alarma do rješavanja, za alarme riješene u ovom razdoblju.',
    alarmMttrDetail_one: '{{count}} riješen alarm',
    alarmMttrDetail_few: '{{count}} riješena alarma',
    alarmMttrDetail_other: '{{count}} riješenih alarma',
    notEnoughAlarms_one: 'nedovoljno podataka ({{count}} riješen alarm)',
    notEnoughAlarms_few: 'nedovoljno podataka ({{count}} riješena alarma)',
    notEnoughAlarms_other: 'nedovoljno podataka ({{count}} riješenih alarma)',
    otherAlarm: 'Ostali alarm',

    alarmHistoryTitle: 'Povijest alarma',
    alarmHistoryLoadFailed: 'Povijest alarma nije moguće učitati. {{message}}',
    alarmTypeFilterAria: 'Filtriraj po vrsti alarma',
    anyType: 'Bilo koja vrsta',
    roomFilterPlaceholder: 'Šifra sobe',
    roomFilterAria: 'Filtriraj po točnoj šifri sobe',
    alarmFromLabel: 'Povijest alarma od',
    alarmFromPlaceholder: 'Od',
    alarmToLabel: 'Povijest alarma do',
    alarmToPlaceholder: 'Do',
    rangeNarrowsList: 'Ovaj raspon sužava samo popis alarma; gornji KPI-jevi prikazuju odabrano unaprijed postavljeno razdoblje.',
    rangeIncomplete: 'Odaberite i početni i završni datum — jedna granica sama nije raspon, pa je i dalje na snazi unaprijed postavljeno razdoblje.',

    noAlarmsTitle: 'Nema alarma u ovom razdoblju',
    noAlarmsDescription: 'Ništa nije podignuto u odabranom razdoblju.',
    loadMore: 'Učitaj još',
    loadedOfTotal: 'Prikazano {{loaded}} od {{total}}',

    columnSeverity: 'Ozbiljnost',
    raised: 'Podignut',
    columnRoom: 'Soba',
    columnAlarm: 'Alarm',
    acknowledgedByLabel: 'Potvrdio/la',
    resolvedLabel: 'Riješen',
    durationLabel: 'Trajanje',
    // Uz oznaku Otvoren ili Potvrđen u retku: „Otvoren … već 1 h 37 min”.
    openFor: 'već {{duration}}',

    statusOpen: 'Otvoren',
    statusAcknowledged: 'Potvrđen',

    // frontOfficeView.ts — alarmHistorySummary
    frontOfficeSummaryCount_one: '{{count}} alarm u {{window}}',
    frontOfficeSummaryCount_few: '{{count}} alarma u {{window}}',
    frontOfficeSummaryCount_other: '{{count}} alarma u {{window}}',
    frontOfficeWindowChosen: 'odabranom razdoblju',
    frontOfficeWindowDays: 'posljednjih {{days}} dana',
    frontOfficeStillOpen: '{{count}} još otvoreno',
    frontOfficeStillOpenOfLoaded: '{{count}} još otvoreno od {{loaded}} učitanih',
  },
};
